import React, { useImperativeHandle, useMemo, useRef } from 'react'
import { useTheme } from 'styled-components'

import { useCanvasPresentationFile } from '../../../../../components/WebRendererCanvas/hooks'

import LoadingState from '../../../components/LoadingState'
import { PageCanvasError } from '../PageCanvasError'
import { WebRendererCanvas } from '../../../../../components/WebRendererCanvas'

import {
  useInitialCameraPosition,
  useSaveLastCameraPositionInLocalStorage,
} from '../../hooks'

import CanvasContextMenu, { useCanvasContextMenu } from '../CanvasContextMenu'
import {
  OriginContent,
  AnnotationsOverlay,
} from '../../../../canvas/components'
import { useCanvasAnnotations } from '../../../../annotations/hooks'

import {
  PageCanvasWrapper,
  StyledContainer,
  StyledPageCanvas,
} from './SharePageCanvas.styles'
import { getWebRenderCursor } from '../../../../inspector'
import { useObjectProxyAccumulator } from '@sketch/utils'
import { DocumentHead, ErrorMessage } from '@sketch/components'
import { ReactComponent as PageCanvasIcon } from '@sketch/icons/page-canvas-64'
import {
  FrameDetailInspectorOverlay,
  useInspectorContext,
  useInspectorHoveredElement,
  FrameDetailInspectorContextMenu,
} from '../../../FrameView'
import { FrameDetailInspectorTestOverlay } from '../../../FrameView/components/FrameDetailInspectorTestOverlay'
import { PageCanvasFramesOverlays } from '../PageCanvasFramesOverlays'
import { useCopyLinkShortcut } from '../../../../canvas/hooks'
import { useShowSingleEntity } from '@sketch-hq/sketch-web-renderer'

const IS_RUNNING_TESTS =
  process.env.JEST_WORKER_ID !== undefined || window.Cypress

interface SharePageCanvasProps {
  permanentPageIdentifier: string
  isViewingLatestVersion?: boolean
  /**
   * When set (single-Section focus view), isolates this entity as the renderer
   * scene root and scopes the canvas overlays to it - its direct child sections
   * plus the frames it contains at any depth. Undefined on the normal page
   * canvas.
   */
  rootEntityUUID?: string
}

export const SharePageCanvas = function SharePageCanvas({
  ref,
  permanentPageIdentifier: pageUUID,
  isViewingLatestVersion,
  rootEntityUUID,
}: SharePageCanvasProps & {
  ref?: React.RefObject<HTMLDivElement | null>
}) {
  const theme = useTheme()
  const wrapperRef = useRef<HTMLDivElement>(
    null
  ) as React.RefObject<HTMLDivElement>

  const {
    share,
    page,
    versionShortId,
    hasError,
    isLoading,
    isRendering,
    showArtboardDecorations,
  } = useCanvasPresentationFile({
    pageUUID,
  })

  const [isViewingSingleEntity] = useShowSingleEntity()

  // The single-Section focus view isolates its Section (so isViewingSingleEntity
  // is true) but still needs the overlays to show the Section's child sections.
  // A single-frame Preview passes no rootEntityUUID, so overlays stay hidden
  // there as before.
  const showFramesOverlays =
    !isViewingSingleEntity || rootEntityUUID !== undefined

  const filePath = page?.presentationFile?.downloadUrl

  // Camera Position
  // Scoped by rootEntityUUID so the single-Section focus view remembers its own
  // position instead of the page's: handing the renderer the page's zoom and pan
  // was overriding the fit it performs when it re-roots the scene onto the
  // Section, and left the page canvas pointing at wherever the Section was.
  const initialCameraPosition = useInitialCameraPosition({
    pageUUID,
    versionShortId,
    rootEntityUUID,
  })

  const { initialZoom, initialPan } = initialCameraPosition

  useSaveLastCameraPositionInLocalStorage({
    pageUUID,
    versionShortId,
    rootEntityUUID,
  })

  // Enable Cmd+shift+C shortcut to copy page/frame URL
  useCopyLinkShortcut()

  // Context Menu
  const {
    contextMenuPos,
    contextMenuMetaData,
    canvasContainerRef,
    handleContextMenu,
    handleCloseContextMenu,
  } = useCanvasContextMenu()

  // Annotations
  const annotationsContext = useCanvasAnnotations()

  const activeAnnotation = annotationsContext?.activeAnnotation
  const isPlacingDraftAnnotation =
    !!annotationsContext?.isPlacingDraftAnnotation
  const isAnnotationMoving = !!annotationsContext?.isAnnotationMoving
  const annotationPointerEvents = annotationsContext
    ? {
        onTouchStart: annotationsContext.onTouchStart,
        onTouchEnd: annotationsContext.onTouchEnd,
        onPointerDown: annotationsContext.onPointerDown,
        onPointerUp: annotationsContext.onPointerUp,
        onMouseDown: annotationsContext.onMouseDown,
        onMouseUp: annotationsContext.onMouseUp,
      }
    : undefined

  const {
    isInspectorActive,
    clickHandler,
    mouseMoveHandler,
    mouseDownHandle,
    selectedElement,
  } = useInspectorContext()

  const hoveredElement = useInspectorHoveredElement()

  const webRenderContainerProps = useObjectProxyAccumulator(
    { onContextMenu: handleContextMenu },
    isInspectorActive
      ? {
          onClick: clickHandler,
          onMouseMove: mouseMoveHandler,
          onMouseDown: mouseDownHandle,
        }
      : annotationPointerEvents || {}
  )

  useImperativeHandle(ref, () => wrapperRef.current!)
  useImperativeHandle(canvasContainerRef, () => wrapperRef.current!)

  const memoizedContextMenuPosition = useMemo(
    () =>
      contextMenuPos
        ? {
            /**
             * On the Artboard view we have a custom context menu
             * that works similar to the already existing "useCanvasContextMenu"
             * so we just map the positions from one to the other
             */
            x: contextMenuPos.posX,
            y: contextMenuPos.posY,
          }
        : null,
    [contextMenuPos]
  )

  if (isLoading || isRendering) {
    return <LoadingState />
  }

  if (hasError) {
    return (
      <PageCanvasError
        description={
          <>
            There was a problem fetching the page data. You can find more
            information on our{' '}
            <a href="https://status.sketch.com/">Status Page</a>.
          </>
        }
        showReloadCanvasAction
      />
    )
  }

  if (!page) {
    return (
      <StyledContainer>
        <ErrorMessage
          icon={<PageCanvasIcon />}
          iconSize="medium"
          description={
            <>
              This page does not exist in the current version. <br />
              Select another page or another version.
            </>
          }
        />
      </StyledContainer>
    )
  }

  /**
   * By this point filePath shouldn't ever be undefined, but we narrow the type
   * to a defined string with this error case just to be sure.
   */
  if (!filePath) {
    return (
      <PageCanvasError
        description={
          <>
            Something went wrong while loading the canvas. Please reload — or
            try again later.
          </>
        }
        showReloadCanvasAction
      />
    )
  }

  /**
   * This error condition is a relic from the early MVP of the canvas that still
   * used the legacy JSON format. As such only internal Sketch users that may
   * still have a JSON PR file associated with their Sketch document will ever
   * see it. Safe to remove in future.
   */
  if (filePath.includes('.json')) {
    return (
      <PageCanvasError
        description={
          <>
            Please save a new document version in Sketch and reload to see this
            page as a canvas.
          </>
        }
        showReloadCanvasAction
      />
    )
  }

  return (
    <PageCanvasWrapper>
      {page?.name && share?.name && (
        <DocumentHead title={`${page.name} - ${share?.name}`} />
      )}

      <StyledPageCanvas ref={wrapperRef}>
        <WebRendererCanvas
          pageUUID={pageUUID}
          page={page}
          versionShortId={versionShortId || undefined}
          shareIdentifier={share?.identifier}
          initialZoom={initialZoom}
          initialPan={initialPan}
          rootEntityUUID={rootEntityUUID}
          backgroundColor={theme.colors.background.canvas.A}
          webRenderCursor={getWebRenderCursor(
            isPlacingDraftAnnotation,
            hoveredElement,
            isAnnotationMoving && !isViewingLatestVersion
          )}
          webRenderContainerProps={webRenderContainerProps.current}
          showArtboardDecorations={showArtboardDecorations}
        >
          <OriginContent>
            {showFramesOverlays && (
              <PageCanvasFramesOverlays
                focusedSectionUUID={rootEntityUUID}
                // Only while inspecting: the selection overlay that stands in
                // for this one is mounted with the inspector, and the selection
                // itself outlives leaving the Inspect tab.
                hiddenEntityUUID={
                  isInspectorActive ? selectedElement?.elementUUID : undefined
                }
              />
            )}

            <AnnotationsOverlay
              activeAnnotationIdentifier={activeAnnotation}
              parentWrapper={canvasContainerRef}
            />
          </OriginContent>

          {isInspectorActive && (
            <>
              {IS_RUNNING_TESTS && <FrameDetailInspectorTestOverlay />}
              <FrameDetailInspectorOverlay
                canvasContainerRef={canvasContainerRef}
              />
            </>
          )}

          {isInspectorActive ? (
            <FrameDetailInspectorContextMenu
              contextMenuPosition={memoizedContextMenuPosition}
              onCloseMenu={handleCloseContextMenu}
            />
          ) : (
            <CanvasContextMenu
              permanentPageId={pageUUID}
              closeTooltip={handleCloseContextMenu}
              contextMenuPos={contextMenuPos}
              contextMenuMetaData={contextMenuMetaData}
            />
          )}
        </WebRendererCanvas>
      </StyledPageCanvas>
    </PageCanvasWrapper>
  )
}
